import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, Check, Send, Heart, Sparkles, MessageCircle, Star, Users, PenLine } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { id: 'experience', title: 'Your Experience', icon: Heart, color: '#C88D74' },
  { id: 'work', title: 'The Work Itself', icon: Sparkles, color: '#8BADA4' },
  { id: 'perception', title: 'Perception Shift', icon: MessageCircle, color: '#9B8EC4' },
  { id: 'overall', title: 'Overall Experience', icon: Star, color: '#D4A27F' },
  { id: 'testimonial', title: 'Testimonial', icon: PenLine, color: '#7CAE7A' },
  { id: 'final', title: 'Final Thoughts', icon: Users, color: '#6BA3BE' },
];

const CHANGES_OPTIONS = [
  'Less pain or tension',
  'More energy',
  'Better sleep',
  'Improved digestion',
  'Emotional shifts',
  'Clearer mind',
  'Felt more connected to my body',
];

const CONSISTENCY_OPTIONS = [
  'Daily',
  'Most days',
  'A few times per week',
  'Occasionally',
];

const RECOMMEND_OPTIONS = ['Yes', 'No', 'Maybe'];
const SHARE_OPTIONS = [
  'Yes — full name',
  'Yes — first name only',
  'Yes — first name & last initial',
  'Keep completely anonymous',
  'No',
];

const CompletionReflection = ({ userId, program = 'fascia-reset' }) => {
  const [currentSection, setCurrentSection] = useState(0);
  const [responses, setResponses] = useState({
    feeling_before: '',
    changes_noticed: [],
    changes_other: '',
    biggest_surprise: '',
    one_sentence: '',
    consistency: '',
    biggest_impact: '',
    want_more: '',
    want_less: '',
    felt_like_beginning: '',
    body_relationship: '',
    is_this_working: '',
    rating: 0,
    would_recommend: '',
    recommend_to_whom: '',
    say_to_uncertain: '',
    before_felt: '',
    during_noticed: '',
    after_feel: '',
    helped_me: '',
    can_share: '',
    own_testimonial: '',
    anything_else: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Load existing reflection
  useEffect(() => {
    if (!userId) return;
    const load = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/completion-reflection/${userId}/${program}`);
        const data = await res.json();
        if (data.reflection) {
          setResponses(prev => ({ ...prev, ...data.reflection.responses }));
          setSubmitted(true);
        }
      } catch (err) {
        console.error('Failed to load reflection', err);
      }
      setLoaded(true);
    };
    load();
  }, [userId, program]);

  const updateResponse = (key, value) => {
    setResponses(prev => ({ ...prev, [key]: value }));
  };

  const toggleCheckbox = (key, value) => {
    setResponses(prev => {
      const arr = prev[key] || [];
      return {
        ...prev,
        [key]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value],
      };
    });
  };

  const handleSubmit = async () => {
    if (!userId) return;
    setSaving(true);
    try {
      await fetch(`${BACKEND_URL}/api/completion-reflection/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, program, responses }),
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Failed to save', err);
    }
    setSaving(false);
  };

  const handleEdit = () => {
    setSubmitted(false);
    setCurrentSection(0);
  };

  const goNext = () => {
    if (currentSection < SECTIONS.length - 1) setCurrentSection(prev => prev + 1);
  };
  const goPrev = () => {
    if (currentSection > 0) setCurrentSection(prev => prev - 1);
  };

  if (!loaded) return null;

  // Already submitted view — celebration!
  if (submitted) {
    return (
      <div className="mt-8" data-testid="reflection-submitted">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="bg-gradient-to-br from-forest/5 via-terracotta/5 to-forest/5 rounded-2xl p-10 border border-stone-200 text-center relative overflow-hidden"
        >
          {/* Floating sparkles */}
          {[...Array(12)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20, scale: 0 }}
              animate={{
                opacity: [0, 1, 0],
                y: [20, -60 - Math.random() * 80],
                scale: [0, 1.2, 0],
                x: [0, (Math.random() - 0.5) * 120],
              }}
              transition={{
                duration: 2 + Math.random() * 1.5,
                delay: 0.3 + i * 0.15,
                repeat: Infinity,
                repeatDelay: 2 + Math.random() * 3,
              }}
              className="absolute pointer-events-none"
              style={{
                left: `${15 + Math.random() * 70}%`,
                top: `${30 + Math.random() * 40}%`,
              }}
            >
              <Sparkles
                size={12 + Math.random() * 12}
                className="text-terracotta/60"
              />
            </motion.div>
          ))}

          {/* Big smiley */}
          <motion.div
            initial={{ scale: 0, rotate: -20 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 200, damping: 12, delay: 0.2 }}
            className="w-24 h-24 bg-gradient-to-br from-terracotta/20 to-terracotta/10 rounded-full flex items-center justify-center mx-auto mb-6 relative"
          >
            <span className="text-5xl" role="img" aria-label="smile">&#x263A;</span>
            <motion.div
              animate={{ scale: [1, 1.3, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="absolute -top-1 -right-1"
            >
              <Sparkles size={20} className="text-terracotta" />
            </motion.div>
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
              className="absolute -bottom-1 -left-1"
            >
              <Sparkles size={16} className="text-forest/60" />
            </motion.div>
          </motion.div>

          <motion.h3
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="font-heading text-3xl text-forest mb-3"
          >
            We've enjoyed you being here
          </motion.h3>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            className="font-heading text-2xl text-terracotta mb-4"
          >
            sooo sooooo much!!!
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="text-stone-600 font-body text-base leading-relaxed max-w-lg mx-auto mb-8"
          >
            We look forward to working with you again soon!
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
          >
            <button
              onClick={handleEdit}
              className="bg-stone-100 text-forest px-5 py-2 rounded-full font-body text-sm hover:bg-stone-200 transition-colors"
              data-testid="edit-reflection-btn"
            >
              Edit My Reflection
            </button>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="mt-8" data-testid="completion-reflection">
      {/* Welcome Message */}
      <div className="bg-gradient-to-br from-forest/5 to-terracotta/5 rounded-2xl p-6 sm:p-8 border border-stone-200 mb-8 text-center">
        <Heart size={28} className="text-terracotta mx-auto mb-4" />
        <h2 className="font-heading text-2xl sm:text-3xl text-forest mb-3">
          We are so glad you joined us for the 28 Day Reset
        </h2>
        <p className="text-stone-600 font-body text-sm sm:text-base leading-relaxed max-w-xl mx-auto">
          We'd love it if you would take a few moments to complete the next few pages
          to help us build amazing new programs or continuations. We value your opinions and experiences!
        </p>
      </div>

      {/* Section Progress Dots */}
      <div className="flex items-center justify-center gap-2 mb-8" data-testid="section-progress">
        {SECTIONS.map((section, idx) => {
          const Icon = section.icon;
          const isActive = idx === currentSection;
          const isComplete = idx < currentSection;
          return (
            <button
              key={section.id}
              onClick={() => setCurrentSection(idx)}
              className="group flex flex-col items-center gap-1"
              data-testid={`section-dot-${section.id}`}
            >
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                  isActive
                    ? 'scale-110 shadow-md'
                    : isComplete
                    ? 'opacity-80'
                    : 'opacity-30'
                }`}
                style={{
                  backgroundColor: isActive || isComplete ? section.color + '20' : '#E6E2DA',
                  border: isActive ? `2px solid ${section.color}` : '2px solid transparent',
                }}
              >
                {isComplete ? (
                  <Check size={14} style={{ color: section.color }} />
                ) : (
                  <Icon size={14} style={{ color: isActive ? section.color : '#9CA099' }} />
                )}
              </div>
              <span className={`text-xs font-body hidden sm:block ${isActive ? 'text-forest' : 'text-stone-400'}`}>
                {section.title}
              </span>
            </button>
          );
        })}
      </div>

      {/* Section Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSection}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.25 }}
        >
          {/* SECTION 1: Your Experience */}
          {currentSection === 0 && (
            <div className="space-y-6" data-testid="section-experience">
              <SectionHeader section={SECTIONS[0]} number={1} />

              <QuestionBlock number="1">
                <p className="font-body text-forest font-medium text-sm mb-1">
                  Before starting the Reset, how were you feeling overall?
                </p>
                <p className="text-stone-400 font-body text-xs mb-3">
                  Physically, mentally, emotionally — a few words or sentences
                </p>
                <textarea
                  value={responses.feeling_before}
                  onChange={e => updateResponse('feeling_before', e.target.value)}
                  className="w-full bg-stone-50 border border-stone-200 rounded-xl p-4 font-body text-sm text-stone-700 resize-none focus:outline-none focus:border-terracotta/50 focus:ring-1 focus:ring-terracotta/20 transition-all"
                  rows={3}
                  placeholder="I was feeling..."
                  data-testid="q-feeling-before"
                />
              </QuestionBlock>

              <QuestionBlock number="2">
                <p className="font-body text-forest font-medium text-sm mb-1">
                  What changes did you notice during or after the Reset?
                </p>
                <p className="text-stone-400 font-body text-xs mb-3">Check all that apply</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {CHANGES_OPTIONS.map(option => (
                    <CheckboxItem
                      key={option}
                      label={option}
                      checked={responses.changes_noticed.includes(option)}
                      onChange={() => toggleCheckbox('changes_noticed', option)}
                      color={SECTIONS[0].color}
                    />
                  ))}
                </div>
                <div className="mt-3">
                  <input
                    type="text"
                    value={responses.changes_other}
                    onChange={e => updateResponse('changes_other', e.target.value)}
                    className="w-full bg-stone-50 border border-stone-200 rounded-lg px-4 py-2.5 font-body text-sm text-stone-700 focus:outline-none focus:border-terracotta/50 transition-all"
                    placeholder="Other changes you noticed..."
                    data-testid="q-changes-other"
                  />
                </div>
              </QuestionBlock>

              <QuestionBlock number="3">
                <p className="font-body text-forest font-medium text-sm mb-3">
                  What surprised you the most about this experience?
                </p>
                <textarea
                  value={responses.biggest_surprise}
                  onChange={e => updateResponse('biggest_surprise', e.target.value)}
                  className="w-full bg-stone-50 border border-stone-200 rounded-xl p-4 font-body text-sm text-stone-700 resize-none focus:outline-none focus:border-terracotta/50 focus:ring-1 focus:ring-terracotta/20 transition-all"
                  rows={3}
                  placeholder="What took me by surprise was..."
                  data-testid="q-biggest-surprise"
                />
              </QuestionBlock>

              <QuestionBlock number="4">
                <p className="font-body text-forest font-medium text-sm mb-3">
                  If you had to describe your experience in one sentence, what would you say?
                </p>
                <input
                  type="text"
                  value={responses.one_sentence}
                  onChange={e => updateResponse('one_sentence', e.target.value)}
                  className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 font-body text-sm text-stone-700 focus:outline-none focus:border-terracotta/50 focus:ring-1 focus:ring-terracotta/20 transition-all"
                  placeholder="In one sentence..."
                  data-testid="q-one-sentence"
                />
              </QuestionBlock>
            </div>
          )}

          {/* SECTION 2: The Work Itself */}
          {currentSection === 1 && (
            <div className="space-y-6" data-testid="section-work">
              <SectionHeader section={SECTIONS[1]} number={2} />

              <QuestionBlock number="5">
                <p className="font-body text-forest font-medium text-sm mb-3">
                  How consistent were you with the practices?
                </p>
                <div className="space-y-2">
                  {CONSISTENCY_OPTIONS.map(option => (
                    <RadioItem
                      key={option}
                      label={option}
                      selected={responses.consistency === option}
                      onChange={() => updateResponse('consistency', option)}
                      color={SECTIONS[1].color}
                    />
                  ))}
                </div>
              </QuestionBlock>

              <QuestionBlock number="6">
                <p className="font-body text-forest font-medium text-sm mb-1">
                  What part of the program had the biggest impact on you?
                </p>
                <p className="text-stone-400 font-body text-xs mb-3">
                  e.g. fascial maneuvers, organ reset, lives, education, community
                </p>
                <textarea
                  value={responses.biggest_impact}
                  onChange={e => updateResponse('biggest_impact', e.target.value)}
                  className="w-full bg-stone-50 border border-stone-200 rounded-xl p-4 font-body text-sm text-stone-700 resize-none focus:outline-none focus:border-terracotta/50 focus:ring-1 focus:ring-terracotta/20 transition-all"
                  rows={3}
                  placeholder="The biggest impact came from..."
                  data-testid="q-biggest-impact"
                />
              </QuestionBlock>

              <QuestionBlock number="7">
                <p className="font-body text-forest font-medium text-sm mb-3">
                  What would you like MORE of?
                </p>
                <textarea
                  value={responses.want_more}
                  onChange={e => updateResponse('want_more', e.target.value)}
                  className="w-full bg-stone-50 border border-stone-200 rounded-xl p-4 font-body text-sm text-stone-700 resize-none focus:outline-none focus:border-terracotta/50 focus:ring-1 focus:ring-terracotta/20 transition-all"
                  rows={2}
                  placeholder="I'd love more..."
                  data-testid="q-want-more"
                />
              </QuestionBlock>

              <QuestionBlock number="8">
                <p className="font-body text-forest font-medium text-sm mb-3">
                  What would you like LESS of or changed?
                </p>
                <textarea
                  value={responses.want_less}
                  onChange={e => updateResponse('want_less', e.target.value)}
                  className="w-full bg-stone-50 border border-stone-200 rounded-xl p-4 font-body text-sm text-stone-700 resize-none focus:outline-none focus:border-terracotta/50 focus:ring-1 focus:ring-terracotta/20 transition-all"
                  rows={2}
                  placeholder="I'd prefer less..."
                  data-testid="q-want-less"
                />
              </QuestionBlock>
            </div>
          )}

          {/* SECTION 3: Perception Shift */}
          {currentSection === 2 && (
            <div className="space-y-6" data-testid="section-perception">
              <SectionHeader section={SECTIONS[2]} number={3} />

              <QuestionBlock number="9">
                <p className="font-body text-forest font-medium text-sm mb-1">
                  At the beginning, did this feel like "exercise," "healing," or something else?
                </p>
                <p className="text-stone-400 font-body text-xs mb-3">And has that changed?</p>
                <textarea
                  value={responses.felt_like_beginning}
                  onChange={e => updateResponse('felt_like_beginning', e.target.value)}
                  className="w-full bg-stone-50 border border-stone-200 rounded-xl p-4 font-body text-sm text-stone-700 resize-none focus:outline-none focus:border-terracotta/50 focus:ring-1 focus:ring-terracotta/20 transition-all"
                  rows={3}
                  placeholder="At first it felt like..."
                  data-testid="q-felt-like-beginning"
                />
              </QuestionBlock>

              <QuestionBlock number="10">
                <p className="font-body text-forest font-medium text-sm mb-3">
                  Did your relationship with your body shift at all? If yes, how?
                </p>
                <textarea
                  value={responses.body_relationship}
                  onChange={e => updateResponse('body_relationship', e.target.value)}
                  className="w-full bg-stone-50 border border-stone-200 rounded-xl p-4 font-body text-sm text-stone-700 resize-none focus:outline-none focus:border-terracotta/50 focus:ring-1 focus:ring-terracotta/20 transition-all"
                  rows={3}
                  placeholder="My relationship with my body..."
                  data-testid="q-body-relationship"
                />
              </QuestionBlock>

              <QuestionBlock number="11">
                <p className="font-body text-forest font-medium text-sm mb-1">
                  At any point did you wonder, "Is this working?"
                </p>
                <p className="text-stone-400 font-body text-xs mb-3">What helped you move through that?</p>
                <textarea
                  value={responses.is_this_working}
                  onChange={e => updateResponse('is_this_working', e.target.value)}
                  className="w-full bg-stone-50 border border-stone-200 rounded-xl p-4 font-body text-sm text-stone-700 resize-none focus:outline-none focus:border-terracotta/50 focus:ring-1 focus:ring-terracotta/20 transition-all"
                  rows={3}
                  placeholder="There was a moment when..."
                  data-testid="q-is-this-working"
                />
              </QuestionBlock>
            </div>
          )}

          {/* SECTION 4: Overall Experience */}
          {currentSection === 3 && (
            <div className="space-y-6" data-testid="section-overall">
              <SectionHeader section={SECTIONS[3]} number={4} />

              <QuestionBlock number="12">
                <p className="font-body text-forest font-medium text-sm mb-4">
                  On a scale of 1–10, how would you rate your experience?
                </p>
                <p className="text-stone-400 font-body text-xs mb-4">1 = not valuable, 10 = life-changing</p>
                <div className="flex gap-2 flex-wrap justify-center">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(val => (
                    <button
                      key={val}
                      onClick={() => updateResponse('rating', val)}
                      className={`w-11 h-11 rounded-xl font-heading text-sm font-bold transition-all ${
                        responses.rating === val
                          ? 'text-white shadow-lg scale-110'
                          : 'bg-stone-50 text-stone-500 hover:bg-stone-100 border border-stone-200'
                      }`}
                      style={responses.rating === val ? { backgroundColor: SECTIONS[3].color } : {}}
                      data-testid={`rating-${val}`}
                    >
                      {val}
                    </button>
                  ))}
                </div>
                {responses.rating > 0 && (
                  <p className="text-center mt-3 font-body text-sm" style={{ color: SECTIONS[3].color }}>
                    {responses.rating <= 3 ? 'Thank you for your honesty' : responses.rating <= 6 ? 'Good foundation to build on' : responses.rating <= 8 ? 'Wonderful!' : 'Incredible — thank you!'}
                  </p>
                )}
              </QuestionBlock>

              <QuestionBlock number="13">
                <p className="font-body text-forest font-medium text-sm mb-3">
                  Would you recommend this program to someone else?
                </p>
                <div className="flex gap-2 flex-wrap">
                  {RECOMMEND_OPTIONS.map(option => (
                    <button
                      key={option}
                      onClick={() => updateResponse('would_recommend', option)}
                      className={`px-5 py-2.5 rounded-full font-body text-sm transition-all ${
                        responses.would_recommend === option
                          ? 'text-white shadow-md'
                          : 'bg-stone-50 text-stone-600 border border-stone-200 hover:bg-stone-100'
                      }`}
                      style={responses.would_recommend === option ? { backgroundColor: SECTIONS[3].color } : {}}
                      data-testid={`recommend-${option.toLowerCase()}`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
                {responses.would_recommend === 'Yes' && (
                  <div className="mt-3">
                    <p className="text-stone-400 font-body text-xs mb-2">Who would you recommend it to?</p>
                    <input
                      type="text"
                      value={responses.recommend_to_whom}
                      onChange={e => updateResponse('recommend_to_whom', e.target.value)}
                      className="w-full bg-stone-50 border border-stone-200 rounded-lg px-4 py-2.5 font-body text-sm text-stone-700 focus:outline-none focus:border-terracotta/50 transition-all"
                      placeholder="I'd recommend it to..."
                      data-testid="q-recommend-to-whom"
                    />
                  </div>
                )}
              </QuestionBlock>

              <QuestionBlock number="14">
                <p className="font-body text-forest font-medium text-sm mb-3">
                  What would you say to someone who is considering joining but unsure?
                </p>
                <textarea
                  value={responses.say_to_uncertain}
                  onChange={e => updateResponse('say_to_uncertain', e.target.value)}
                  className="w-full bg-stone-50 border border-stone-200 rounded-xl p-4 font-body text-sm text-stone-700 resize-none focus:outline-none focus:border-terracotta/50 focus:ring-1 focus:ring-terracotta/20 transition-all"
                  rows={3}
                  placeholder="I'd tell them..."
                  data-testid="q-say-to-uncertain"
                />
              </QuestionBlock>
            </div>
          )}

          {/* SECTION 5: Testimonial */}
          {currentSection === 4 && (
            <div className="space-y-6" data-testid="section-testimonial">
              <SectionHeader section={SECTIONS[4]} number={5} />

              <div className="bg-forest/5 rounded-xl p-5 border border-forest/10 mb-2">
                <p className="font-body text-forest/80 text-sm leading-relaxed italic">
                  If you're open to it, we'd love to share your experience to help others.
                  We've given a few prompts if you wish. You can keep this simple or heartfelt — whatever feels true.
                </p>
              </div>

              <QuestionBlock number="15">
                <p className="font-body text-forest font-medium text-sm mb-4">
                  Before — During — After
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="font-body text-xs text-stone-400 mb-1 block">Before the Reset, I felt:</label>
                    <input
                      type="text"
                      value={responses.before_felt}
                      onChange={e => updateResponse('before_felt', e.target.value)}
                      className="w-full bg-stone-50 border border-stone-200 rounded-lg px-4 py-2.5 font-body text-sm text-stone-700 focus:outline-none focus:border-terracotta/50 transition-all"
                      placeholder="Tired, disconnected, in pain..."
                      data-testid="q-before-felt"
                    />
                  </div>
                  <div>
                    <label className="font-body text-xs text-stone-400 mb-1 block">During the Reset, I noticed:</label>
                    <input
                      type="text"
                      value={responses.during_noticed}
                      onChange={e => updateResponse('during_noticed', e.target.value)}
                      className="w-full bg-stone-50 border border-stone-200 rounded-lg px-4 py-2.5 font-body text-sm text-stone-700 focus:outline-none focus:border-terracotta/50 transition-all"
                      placeholder="Subtle shifts, moments of ease..."
                      data-testid="q-during-noticed"
                    />
                  </div>
                  <div>
                    <label className="font-body text-xs text-stone-400 mb-1 block">After the Reset, I now feel:</label>
                    <input
                      type="text"
                      value={responses.after_feel}
                      onChange={e => updateResponse('after_feel', e.target.value)}
                      className="w-full bg-stone-50 border border-stone-200 rounded-lg px-4 py-2.5 font-body text-sm text-stone-700 focus:outline-none focus:border-terracotta/50 transition-all"
                      placeholder="More present, lighter, stronger..."
                      data-testid="q-after-feel"
                    />
                  </div>
                </div>
              </QuestionBlock>

              <QuestionBlock number="16">
                <p className="font-body text-forest font-medium text-sm mb-3">
                  Complete this sentence:
                </p>
                <div className="flex items-start gap-2">
                  <span className="font-accent text-lg text-terracotta italic mt-2 flex-shrink-0">"This program helped me</span>
                  <input
                    type="text"
                    value={responses.helped_me}
                    onChange={e => updateResponse('helped_me', e.target.value)}
                    className="flex-1 bg-stone-50 border-b-2 border-stone-300 px-2 py-2 font-body text-sm text-stone-700 focus:outline-none focus:border-terracotta transition-all bg-transparent"
                    placeholder="..."
                    data-testid="q-helped-me"
                  />
                  <span className="font-accent text-lg text-terracotta italic mt-2">"</span>
                </div>
              </QuestionBlock>

              <QuestionBlock number="17">
                <p className="font-body text-forest font-medium text-sm mb-3">
                  Can we share your words publicly?
                </p>
                <div className="flex gap-2 flex-wrap">
                  {SHARE_OPTIONS.map(option => (
                    <button
                      key={option}
                      onClick={() => updateResponse('can_share', option)}
                      className={`px-4 py-2 rounded-full font-body text-sm transition-all ${
                        responses.can_share === option
                          ? 'text-white shadow-md'
                          : 'bg-stone-50 text-stone-600 border border-stone-200 hover:bg-stone-100'
                      }`}
                      style={responses.can_share === option ? { backgroundColor: SECTIONS[4].color } : {}}
                      data-testid={`share-${option.toLowerCase().replace(/[^a-z]/g, '-')}`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </QuestionBlock>

              <QuestionBlock>
                <p className="font-body text-forest font-medium text-sm mb-1">
                  Or... your own words testimonial
                </p>
                <p className="text-stone-400 font-body text-xs mb-3">We would love to hear it.</p>
                <textarea
                  value={responses.own_testimonial}
                  onChange={e => updateResponse('own_testimonial', e.target.value)}
                  className="w-full bg-stone-50 border border-stone-200 rounded-xl p-4 font-body text-sm text-stone-700 resize-none focus:outline-none focus:border-terracotta/50 focus:ring-1 focus:ring-terracotta/20 transition-all"
                  rows={4}
                  placeholder="In your own words..."
                  data-testid="q-own-testimonial"
                />
              </QuestionBlock>
            </div>
          )}

          {/* SECTION 6: Final Thoughts */}
          {currentSection === 5 && (
            <div className="space-y-6" data-testid="section-final">
              <SectionHeader section={SECTIONS[5]} number={6} />

              <QuestionBlock>
                <p className="font-body text-forest font-medium text-sm mb-3">
                  Anything else you'd like to share?
                </p>
                <textarea
                  value={responses.anything_else}
                  onChange={e => updateResponse('anything_else', e.target.value)}
                  className="w-full bg-stone-50 border border-stone-200 rounded-xl p-4 font-body text-sm text-stone-700 resize-none focus:outline-none focus:border-terracotta/50 focus:ring-1 focus:ring-terracotta/20 transition-all"
                  rows={4}
                  placeholder="Anything at all..."
                  data-testid="q-anything-else"
                />
              </QuestionBlock>

              {/* Submit */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-forest/5 to-terracotta/5 rounded-2xl p-6 border border-stone-200 text-center"
              >
                <p className="font-accent text-lg text-forest/80 italic mb-4">
                  "Thank you for trusting this process — and for trusting yourself."
                </p>
                <button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="bg-forest text-cream px-8 py-3 rounded-full font-body font-medium hover:bg-forest-dark transition-all flex items-center gap-2 mx-auto disabled:opacity-50"
                  data-testid="submit-reflection"
                >
                  <Send size={16} />
                  {saving ? 'Saving...' : 'Submit My Reflection'}
                </button>
              </motion.div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8 pt-6 border-t border-stone-100">
        <button
          onClick={goPrev}
          disabled={currentSection === 0}
          className="flex items-center gap-1 text-stone-400 hover:text-forest font-body text-sm transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid="prev-section"
        >
          <ChevronLeft size={16} />
          Previous
        </button>
        <span className="font-body text-xs text-stone-400">
          {currentSection + 1} of {SECTIONS.length}
        </span>
        <button
          onClick={goNext}
          disabled={currentSection === SECTIONS.length - 1}
          className="flex items-center gap-1 text-forest hover:text-forest-dark font-body text-sm font-medium transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid="next-section"
        >
          Next
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

// Sub-components
const SectionHeader = ({ section, number }) => {
  const Icon = section.icon;
  return (
    <div className="flex items-center gap-3 mb-2">
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: section.color + '20' }}
      >
        <Icon size={16} style={{ color: section.color }} />
      </div>
      <div>
        <p className="text-stone-400 font-body text-xs">Section {number}</p>
        <h3 className="font-heading text-lg text-forest">{section.title}</h3>
      </div>
    </div>
  );
};

const QuestionBlock = ({ number, children }) => (
  <div className="bg-white rounded-xl border border-stone-100 p-5 relative">
    {number && (
      <div className="absolute -left-3 top-5 w-6 h-6 bg-forest text-cream rounded-full flex items-center justify-center">
        <span className="font-body text-xs font-bold">{number}</span>
      </div>
    )}
    <div className={number ? 'ml-2' : ''}>{children}</div>
  </div>
);

const CheckboxItem = ({ label, checked, onChange, color }) => (
  <button
    onClick={onChange}
    className={`flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
      checked ? 'border-transparent shadow-sm' : 'border-stone-100 hover:bg-stone-50'
    }`}
    style={checked ? { backgroundColor: color + '10', borderColor: color + '30' } : {}}
  >
    <div
      className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
        checked ? 'border-transparent' : 'border-stone-300'
      }`}
      style={checked ? { backgroundColor: color } : {}}
    >
      {checked && <Check size={12} className="text-white" />}
    </div>
    <span className={`font-body text-sm ${checked ? 'text-forest font-medium' : 'text-stone-600'}`}>
      {label}
    </span>
  </button>
);

const RadioItem = ({ label, selected, onChange, color }) => (
  <button
    onClick={onChange}
    className={`flex items-center gap-3 p-3 rounded-lg border transition-all text-left w-full ${
      selected ? 'border-transparent shadow-sm' : 'border-stone-100 hover:bg-stone-50'
    }`}
    style={selected ? { backgroundColor: color + '10', borderColor: color + '30' } : {}}
  >
    <div
      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
        selected ? 'border-transparent' : 'border-stone-300'
      }`}
      style={selected ? { backgroundColor: color } : {}}
    >
      {selected && <div className="w-2 h-2 bg-white rounded-full" />}
    </div>
    <span className={`font-body text-sm ${selected ? 'text-forest font-medium' : 'text-stone-600'}`}>
      {label}
    </span>
  </button>
);

export default CompletionReflection;
